import React from 'react';
import { PiggyBank, DollarSign, ShoppingCart, LineChart, ArrowUp, ArrowDown, Minus } from 'lucide-react';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';

export interface StatCardProps {
  title: string;
  value: string;
  change?: string;
  changeType?: 'positive' | 'negative' | 'neutral';
  icon: 'wallet' | 'dollar' | 'cart' | 'chart';
  isLoading?: boolean;
  transactionCount?: number;
  // Link props
  dateFrom?: string;
  dateTo?: string;
  cardType?: 'income' | 'expenses' | 'savings' | 'networth';
  accountIds?: string[];
  incomeCategoryIds?: string[];
  expenseAccountIds?: string[];
  expenseCategoryIds?: string[];
}

const iconMap = {
  wallet: PiggyBank,
  dollar: DollarSign,
  cart: ShoppingCart,
  chart: LineChart,
};

const iconColors = {
  wallet: 'bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400',
  dollar: 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
  cart: 'bg-orange-50 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
  chart: 'bg-gray-200 text-gray-600 dark:bg-green-900/30 dark:text-green-400',
};

export function StatCard({ 
  title, 
  value, 
  change, 
  changeType = 'neutral',
  icon,
  isLoading = false,
  transactionCount,
  dateFrom,
  dateTo,
  cardType,
  accountIds,
  incomeCategoryIds,
  expenseAccountIds,
  expenseCategoryIds
}: StatCardProps): React.ReactElement {
  const navigate = useNavigate();
  const IconComponent = iconMap[icon];
  const iconColorClass = iconColors[icon];

  const changeColor = 
    changeType === 'positive' 
      ? 'text-green-600 dark:text-green-400' 
      : changeType === 'negative' 
      ? 'text-red-600 dark:text-red-400' 
      : 'text-gray-600 dark:text-gray-400';

  const ChangeIcon = 
    changeType === 'positive' 
      ? ArrowUp 
      : changeType === 'negative' 
      ? ArrowDown 
      : Minus;

  const handleClick = () => {
    // Net Worth card links to Accounts page
    if (cardType === 'networth') {
      navigate('/accounts');
      return;
    }

    // Other cards require date range
    if (!cardType || !dateFrom || !dateTo) {
      return;
    }

    const params = new URLSearchParams();
    
    // Add date range
    params.set('dateFrom', dateFrom);
    params.set('dateTo', dateTo);

    // Add filters based on card type
    if (cardType === 'income') {
      // Income: filter by income category IDs (matches how income is calculated)
      // Income is calculated from transactions with category type='income', not transaction type='credit'
      if (incomeCategoryIds && incomeCategoryIds.length > 0) {
        params.set('categories', incomeCategoryIds.join(','));
      }
    } else if (cardType === 'expenses') {
      // Expenses: filter by debit transactions, expense account IDs, and non-excluded expense categories
      // Expenses are calculated from BANK_CASH, CREDIT_CARD, and LOAN accounts only
      // and exclude uncategorized transactions and excluded categories
      params.set('type', 'debit');
      if (expenseAccountIds && expenseAccountIds.length > 0) {
        params.set('accountIds', expenseAccountIds.join(','));
      }
      // Filter by non-excluded expense categories to match calculation
      // This excludes uncategorized transactions (no category_id match) and excluded categories
      if (expenseCategoryIds && expenseCategoryIds.length > 0) {
        params.set('categories', expenseCategoryIds.join(','));
      }
    } else if (cardType === 'savings' && accountIds && accountIds.length > 0) {
      // Savings: filter by all savings account IDs
      // Savings calculation includes ALL savings accounts, so we need to filter by all of them
      params.set('accountIds', accountIds.join(','));
    }

    navigate(`/transactions?${params.toString()}`);
  };

  const isClickable = cardType === 'networth' || ((cardType === 'income' || cardType === 'expenses' || cardType === 'savings') && dateFrom && dateTo);

  return (
    <div 
      className={clsx(
        cardType === 'networth' 
          ? 'bg-gray-100 dark:bg-gray-800' 
          : 'bg-white dark:bg-gray-800',
        'rounded-xl border border-gray-200 dark:border-gray-700 p-6 transition-shadow',
        isClickable && 'hover:shadow-lg cursor-pointer'
      )}
      onClick={isClickable ? handleClick : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      } : undefined}
      aria-label={isClickable ? (cardType === 'networth' ? 'View accounts' : `View ${title.toLowerCase()} transactions`) : undefined}
    >
      <div className="flex items-center justify-between mb-4">
        <div className={clsx('w-12 h-12 rounded-lg flex items-center justify-center', iconColorClass)}>
          <IconComponent className="h-6 w-6" aria-hidden="true" />
        </div>
        {change && (
          <div className={clsx('flex items-center space-x-1', changeColor)}>
            <ChangeIcon className="h-4 w-4" aria-hidden="true" />
            <span className="text-sm font-medium">{change}</span>
          </div>
        )}
      </div>
      {isLoading ? (
        <div className="space-y-2">
          <div className="h-8 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
          <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        </div>
      ) : (
        <>
          <h3 className={clsx(
            'text-2xl font-bold mb-1',
            cardType === 'networth' 
              ? 'text-green-700 dark:text-gray-100' 
              : 'text-gray-900 dark:text-gray-100'
          )}>{value}</h3>
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            {title}
            {transactionCount !== undefined && (
              transactionCount > 0 
                ? ` • ${transactionCount} ${transactionCount === 1 ? 'transaction' : 'transactions'}`
                : ' • No transactions'
            )}
          </p>
        </>
      )}
    </div>
  );
}

